import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { JSX, useState } from "react";
import {
    FaChevronDown,
    FaChevronRight,
    FaClock,
    FaCogs,
    FaEdit,
    FaEnvelope,
    FaEnvelopeOpenText,
    FaEye,
    FaEyeSlash,
    FaLongArrowAltRight,
    FaMap,
    FaPlus,
    FaRegCalendarAlt,
    FaShareAlt,
    FaSitemap,
    FaSpinner,
    FaTrashAlt,
    FaUsersCog,
    FaUserShield
} from "react-icons/fa";
import { FaCheckToSlot, FaDiagramProject, FaUserLarge, FaUsersGear } from "react-icons/fa6";
import { IoSettingsOutline } from "react-icons/io5";
import { SiGoogleanalytics } from "react-icons/si";
import {
    AuditActionType,
    AuditDetails,
    AuditItem,
    AuditObjectTypes
} from "../../_core/_model.ts";
import {
    GetObjectAuditDetailss
} from "../../_core/_request.ts";
import { AccessTypes, getRoleAccessLabel, LabelConstants, Separators } from "../../Constants.ts";
import { useAppContext } from "../../context/AppContext.tsx";
import { getFriendlyDate, getReadableCron, getTextFromHtml } from "../../Utilities/Utilities.ts";
import RoleCondition from "../Roles/RoleCondition.tsx";
import TooltipButton from "../Tools/TooltipButton.tsx";
import UserCell from "../Tools/UserCell.tsx";

const ExpandableText = ({ text }: { text: any }) => {
    const [expanded, setExpanded] = useState(false);

    if (!text) return null;

    // FIX: If text is a JSX element (like our new Personas layout or Actions),
    // render it directly without the <pre> clipping logic.
    if (typeof text !== "string") {
        return (
            <div style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                {text}
            </div>
        );
    }

    const lines = text.split(/\r?\n/);
    const firstTwoLines = lines.slice(0, 2).join("\n");
    const shouldTruncate = lines.length > 2 || (lines.length === 2 && firstTwoLines.length < text.length - 10);

    const displayText = expanded || !shouldTruncate
        ? text
        : firstTwoLines + "...";

    return (
        <div className="position-relative">
            <pre className="m-0" style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: expanded ? "none" : "4.5em",
                overflow: "hidden",
                fontFamily: "inherit"
            }}>{displayText}</pre>
            {shouldTruncate && (
                <div>
                    <button className="btn btn-sm text-primary mt-1 d-flex align-items-center gap-1 ps-0" onClick={() => setExpanded(!expanded)}>
                        {expanded ? <FaEyeSlash /> : <FaEye />}
                        {expanded ? "Hide" : "View More"}
                    </button>
                </div>
            )}
        </div>
    );
};


const getObjectIcon = (type: AuditObjectTypes): JSX.Element => {
    switch (type) {
        case AuditObjectTypes.User:
            return <FaUserLarge />;
        case AuditObjectTypes.Role:
            return <FaUsersGear />;
        case AuditObjectTypes.RoleGroup:
            return <FaSitemap />;
        case AuditObjectTypes.Settings:
            return <IoSettingsOutline />;
        case AuditObjectTypes.Report:
            return <SiGoogleanalytics />;
        case AuditObjectTypes.UserRequest:
            return <FaCheckToSlot />;
        case AuditObjectTypes.ReportDistributions:
            return <FaShareAlt />;
        case AuditObjectTypes.EmailConfig:
            return <FaEnvelope />;
        case AuditObjectTypes.EmailTemplate:
            return <FaEnvelopeOpenText />;
        case AuditObjectTypes.Dimensions:
            return <FaMap />;
        case AuditObjectTypes.ADConfig:
            return <FaCogs />;
        case AuditObjectTypes.Flow:
            return <FaDiagramProject />;
        default:
            return <FaClock />;
    }
};

const getFieldName = (fldName: string, audit: AuditItem) => {
    let fname = fldName;
    if (audit.forObject == AuditObjectTypes.User) {
        fname = LabelConstants.userLabels[fldName] || fldName;
    } else if (audit.forObject == AuditObjectTypes.Role) {
        fname = LabelConstants.roleLabels[fldName] || fldName;
    } else if (audit.forObject == AuditObjectTypes.Report) {
        fname = LabelConstants.reportsLabel[fldName] || fldName;
    } else if (audit.forObject == AuditObjectTypes.RoleGroup) {
        fname = LabelConstants.roleGroupLabels[fldName] || fldName;
    } else if (audit.forObject == AuditObjectTypes.ReportDistributions) {
        fname = LabelConstants.reportDiestributionLabels[fldName] || fldName;
    } else if (audit.forObject == AuditObjectTypes.UserRequest) {
        fname = LabelConstants.userRequestLabel[fldName] || fldName;
    } else if (fldName === "createdAt") {
        fname = "Created/Updated on";
    } else if (fldName === "isActive") {
        fname = "Status";
    }
    return fname;
};

const getActionIcon = (actionType: number) => {
    switch (actionType) {
        case AuditActionType.Add: return { icon: <FaPlus className="text-success me-2" />, label: "Added" };
        case AuditActionType.Update: return { icon: <FaEdit className="text-primary me-2" />, label: "Updated" };
        case AuditActionType.Delete: return { icon: <FaTrashAlt className="text-danger me-2" />, label: "Deleted Instance" };
        case AuditActionType.UserRole: return { icon: <FaUserShield className="text-warning me-2" />, label: "User Role Changed" };
        case AuditActionType.UserRoleGroup: return { icon: <FaUsersCog className="text-info me-2" />, label: "Role Group Updated" };
        default: return { icon: <FaEdit className="text-muted-theme me-2" />, label: "Modified" };
    }
};

const getValue = (
    fldName: string,
    val: string,
    audit: AuditItem,
    allUsers: any[],
    roles: any[],
    allRoleGroups: any[],
    dimensions: any[]
) => {
    let nVal: any = val;
    if (["UserRole", "Roles", "roleIDS"].includes(fldName)) {
        const rArr = val.split(",");
        nVal = roles
            .filter((x) => x.roleID != null && rArr.includes(x.roleID.toString()))
            .map((x) => x.roleName)
            .join(", ");
    } else if (["roleGroups", "RoleRoleGroup", "UserRoleGroup"].includes(fldName)) {
        const rArr = val.split(",");
        nVal = allRoleGroups
            .filter((x) => rArr.includes(x.roleGroupID.toString()))
            .map((x) => x.roleGroupName)
            .join(", ");
    } else if (fldName === "filterConditions") {
        return val.split(Separators.row).map((condition, idx) =>
            condition.length > 0 ? <RoleCondition key={idx} condition={condition} /> : null
        );
    } else if (
        fldName === "createdAt" ||
        fldName.toLowerCase().endsWith("date")
    ) {
        nVal = getFriendlyDate(val);
    } else if (fldName === "status") {
        nVal = val ? val.charAt(0).toUpperCase() + val.slice(1) : "";
    } else if (fldName === "dataMapCode") {
        const nDim = dimensions.find((x) => x.dimensionCode === val);
        nVal = nDim ? nDim.dimensionName : "";
    } else if (["createdBy", "userID", "byUser", "managerUserID", "userRequestedBy", "userApprovedBy"].includes(fldName)) {
        const u = allUsers.find((x) => x.userID.toString() === val);
        nVal = u ? `${u.userFirstName} ${u.userLastName}` : "";
    } else if (fldName === "weight") {
        nVal = getRoleAccessLabel(val);
    } else if (fldName === "applicationAccess") {
        const a = AccessTypes.find((x) => x.id.toString() === val);
        nVal = a ? a.value : "";
    } else if (fldName === "distributionSchedule") {
        nVal = getReadableCron(val);
    } else if (fldName === "templateDetail") {
        nVal = getTextFromHtml(val);
    } else if (fldName === "isActive") {
        nVal = (val === "1" || val === "True") ? "Active" : "Inactive";
    } else if (fldName === "isApprover") {
        nVal = val === "1" ? "Yes" : "No";
    }
    else if (fldName == "action") {
        nVal = val === "1" ? <span className="text-primary fw-bold">Approved</span> :
            val === "2" ? <span className="text-primary fw-bold">Partially Approved</span> :
                <span className="text-danger fw-bold">Rejected</span>;
    }
    else if (fldName == "isAdUser") {
        nVal = val === "1" ? "Yes" : "No";
    }
    else if (fldName === "personas") {
        if (!val) return "";

        // 181 = µ (Persona Separator), 180 = ´ (Within Persona Separator)
        const separatorPersona = String.fromCharCode(181);
        const separatorField = String.fromCharCode(180);

        // Parses strings like "[Roles:4301:RB_PL_10290|4402:RB_BU_011]" 
        // and extracts ONLY the names (everything after the first colon)
        const parseItems = (str: string, type: string) => {
            if (!str) return [];
            const prefix = `[${type}:`;
            if (str.startsWith(prefix)) {
                const inner = str.substring(prefix.length, str.length - 1);
                if (inner === "None" || !inner) return [];
                return inner.split("|").map(item => {
                    const colonIdx = item.indexOf(":");
                    return colonIdx > -1 ? item.substring(colonIdx + 1) : item;
                });
            }
            return [];
        };

        const personasArr = val.split(separatorPersona).map(pStr => {
            const parts = pStr.split(separatorField);
            return {
                // parts[0] is the Persona ID, which we are intentionally ignoring now
                name: parts[1] || "",
                roles: parseItems(parts[2], "Roles"),
                groups: parseItems(parts[3], "Groups")
            };
        });

        // If only ONE persona, render just the Roles/Groups
        if (personasArr.length === 1) {
            const singlePersona = personasArr[0];
            nVal = (
                <div className="mt-1" style={{ fontFamily: "inherit" }}>
                    {singlePersona.roles.length > 0 && (
                        <div className="mb-1">
                            <strong className="text-muted-theme me-2" style={{ fontSize: "0.85em" }}>Roles:</strong>
                            <span>{singlePersona.roles.join(", ")}</span>
                        </div>
                    )}
                    {singlePersona.groups.length > 0 && (
                        <div>
                            <strong className="text-muted-theme me-2" style={{ fontSize: "0.85em" }}>Groups:</strong>
                            <span>{singlePersona.groups.join(", ")}</span>
                        </div>
                    )}
                </div>
            );
        }
        // If MULTIPLE personas, loop through and render Headers + Roles/Groups
        else {
            nVal = (
                <div className="d-flex flex-column gap-2 mt-2" style={{ fontFamily: "inherit" }}>
                    {personasArr.map((p, pIdx) => (
                        <div key={pIdx} className="border-bottom pb-2">
                            <div className="fw-bold mb-1 text-dark">
                                {p.name}
                            </div>
                            {p.roles.length > 0 && (
                                <div className="mb-1">
                                    <strong className="text-muted-theme me-2" style={{ fontSize: "0.85em" }}>Roles:</strong>
                                    <span>{p.roles.join(", ")}</span>
                                </div>
                            )}
                            {p.groups.length > 0 && (
                                <div>
                                    <strong className="text-muted-theme me-2" style={{ fontSize: "0.85em" }}>Groups:</strong>
                                    <span>{p.groups.join(", ")}</span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            );
        }
    }
    return nVal;
};


interface AuditCardProps {
    audit: AuditItem;
    name: string;
}

const AuditCard = ({ audit, name }: AuditCardProps) => {
    const [expanded, setExpanded] = useState(false);
    const { allUsers, roles, dimensions, allRoleGroups } = useAppContext();
    const [itemDetails, setItemDetails] = useState<AuditDetails[] | null>([]);
    const actionMeta = getActionIcon(audit.actionType);

    const loadMyDetails = () => {
        setItemDetails(null);
        GetObjectAuditDetailss(audit.aAuditItemId)
            .then((x) => setItemDetails([...x.data]))
            .catch(() => setItemDetails([]));
    };

    const toggleExpanded = () => {
        if (!expanded) loadMyDetails();
        setExpanded(!expanded);
    };

    const showPrevious = (change: AuditDetails) => {
        return (
            change.previousValue &&
            change.previousValue !== change.value &&
            change.fieldName.toLowerCase() !== "createdat"
        );
    };

    const notShowField = (change: AuditDetails) => {
        if (["createdAt", "createdBy", "password", "passwordHash"].indexOf(change.fieldName) == -1) {
            let prevValue = getValue(change.fieldName, change.previousValue, audit, allUsers, roles, allRoleGroups, dimensions)
            if (prevValue == "") {
                let v = getValue(change.fieldName, change.value, audit, allUsers, roles, allRoleGroups, dimensions);
                if (v == "")
                    return true;
            }
            return false;
        }
        return true;
    }

    return (
        <div className={clsx("card mb-3 shadow-sm border", expanded && "shadow")} style={{ borderColor: actionMeta.icon.props.className.includes("success") ? '#198754' : actionMeta.icon.props.className.includes("danger") ? '#dc3545' : '#0d6efd' }}>
            <div
                className="card-body d-flex flex-column gap-2 cursor-pointer"
                onClick={toggleExpanded}
            >
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex gap-3 align-items-start">
                        {/* <div>{getObjectIcon(audit.forObject)}</div> */}
                        <div className="d-flex flex-column align-items-start">
                            <div className="d-flex flex-row align-items-start gap-1">
                                <UserCell exUI={undefined} {...{
                                    userId: audit.byUser
                                }} />
                                <div className="d-inline-flex flex-row align-items-start gap-1">
                                    <span >
                                        {
                                            audit.actionType == AuditActionType.Add ?
                                                "has added" : (audit.actionType == AuditActionType.Delete) ?
                                                    "has deleted an item" :
                                                    (audit.forObject == AuditObjectTypes.UserRequest) ? "has Approved/Rejected" :
                                                        "has changed"
                                        }
                                    </span>
                                    <span >
                                        {name && name != "" && <span className="text-muted-theme">{name}</span>}
                                    </span>
                                </div>
                            </div>
                            <div
                                className="d-flex align-items-center gap-1 text-muted-theme ms-4"
                                style={{ fontSize: '0.75rem' }}
                            >
                                <FaRegCalendarAlt />
                                {
                                    getFriendlyDate(audit.onDate)
                                }
                            </div>
                        </div>
                    </div>
                    <div>
                        {expanded ? (
                            <FaChevronDown className="text-muted-theme" />
                        ) : (
                            <FaChevronRight className="text-muted-theme" />
                        )}
                    </div>
                </div>
                {expanded && (
                    itemDetails == null ? (
                        <div className="d-flex flex-column align-items-center text-success mt-4">
                            <AnimatePresence>
                                <motion.div
                                    animate={{ rotate: 360 }}
                                    style={{
                                        display: 'inline-block',
                                        width: '40px',
                                        height: '40px',
                                        marginBottom: '10px'
                                    }}
                                    transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                                >
                                    <FaSpinner className="fa-spin mb-2" size={32} style={{
                                        fontSize: '2.5rem',
                                        color: '#198754',
                                        width: '100%',
                                        height: '100%'
                                    }} />
                                </motion.div>
                            </AnimatePresence>
                            <span>Loading Audits...</span>
                        </div>
                    ) : (
                        <div className="mt-3 border-top pt-2">
                            <ul className="list-unstyled ms-3 mb-0">
                                {itemDetails.map((change, index) => (
                                    !notShowField(change) &&
                                    <li key={index} className="mb-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="text-nowrap" style={{ lineHeight: "18px" }}>{getFieldName(change.fieldName, audit)}</span>
                                            {showPrevious(change) ? (
                                                <div style={{ flex: 1 }}>
                                                    <div className="d-flex flex-wrap align-items-center gap-1">
                                                        <TooltipButton title="Previous value">
                                                            <div className="bg-gray px-2 py-1 text-decoration-line-through"
                                                                style={{
                                                                    display: "inline-block",
                                                                    maxWidth: "100%",
                                                                    wordBreak: "break-word",
                                                                    overflowWrap: "break-word",
                                                                    whiteSpace: "normal",
                                                                }}>
                                                                <ExpandableText text={getValue(change.fieldName, change.previousValue, audit, allUsers, roles, allRoleGroups, dimensions) || "—"} />
                                                            </div>
                                                        </TooltipButton>
                                                        <FaLongArrowAltRight style={{ color: "#FFA500", marginTop: "-4px" }} />
                                                        <TooltipButton title="New value">
                                                            <div className="bg-light-green text-primary px-2 py-1" style={{
                                                                display: "inline-block",
                                                                maxWidth: "100%",
                                                                wordBreak: "break-word",
                                                                overflowWrap: "break-word",
                                                                whiteSpace: "normal",
                                                            }}>
                                                                <ExpandableText text={getValue(change.fieldName, change.value, audit, allUsers, roles, allRoleGroups, dimensions) || "—"} />

                                                            </div>
                                                        </TooltipButton>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="bg-light-green text-primary px-2 py-1" style={{
                                                    display: "inline-block",
                                                    maxWidth: "100%",
                                                    wordBreak: "break-word",
                                                    overflowWrap: "break-word",
                                                    whiteSpace: "normal",
                                                }}>
                                                    <ExpandableText text={getValue(change.fieldName, change.value, audit, allUsers, roles, allRoleGroups, dimensions) || "—"} />
                                                </div>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )
                )}
            </div>
        </div >
    );
};

export default AuditCard;